// src/lib/services/sessionService.ts
// Session tracking — records user sessions for admin visibility.

import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

export interface Session {
  id?: string;
  uid: string;
  startedAt: Timestamp;
  lastSeenAt: Timestamp;
  endedAt?: Timestamp;
  status: 'active' | 'ended';
  userAgent?: string;
}

const SESSIONS_COLLECTION = 'sessions';

/** Create a new session doc */
export async function startSession(uid: string): Promise<string> {
  const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), {
    uid,
    startedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
    status: 'active',
    userAgent: navigator.userAgent || '',
  });
  return docRef.id;
}

/** Update session heartbeat */
export async function heartbeatSession(sessionId: string): Promise<void> {
  await updateDoc(doc(db, SESSIONS_COLLECTION, sessionId), {
    lastSeenAt: serverTimestamp(),
  });
}

/** Mark session as ended */
export async function endSession(sessionId: string): Promise<void> {
  await updateDoc(doc(db, SESSIONS_COLLECTION, sessionId), {
    endedAt: serverTimestamp(),
    status: 'ended',
  });
}

/** Get recent sessions for a user (admin use) */
export async function getRecentSessions(uid: string, max = 10): Promise<Session[]> {
  try {
    const q = query(
      collection(db, SESSIONS_COLLECTION),
      where('uid', '==', uid),
      orderBy('startedAt', 'desc'),
      limit(max),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Session[];
  } catch (err) {
    console.error('[sessionService] getRecentSessions error:', err);
    return [];
  }
}
